import React, { useState, useEffect } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faPlus,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import "../Circularpage/Circularpage.css";
import AddCircular from "../AddCircular/AddCircular";
import ViewCircular from "../ViewCircular/ViewCircular";

const API_URL = "https://aadyaacademy.org/api";

const Circularpage = () => {
  // Set default selectedDate to today
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedCategory, setSelectedCategory] = useState("");
  const [circularData, setCircularData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCircular, setCurrentCircular] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddCircularOpen, setIsAddCircularOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch circulars + categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [circularsResponse, categoriesResponse] = await Promise.all([
          axios.get(`${API_URL}/circulars`),
          axios.get(`${API_URL}/circular-categories`),
        ]);

        setCircularData(
          circularsResponse.data
            .map((item) => ({
              ...item,
              date: moment(item.fromdate || item.todate, [
                "YYYY-MM-DD",
                "DD/MM/YYYY",
              ]).format("YYYY-MM-DD"),
            }))
            .sort((a, b) => {
              const dateA = moment(a.date);
              const dateB = moment(b.date);
              if (dateB.isAfter(dateA)) return 1;
              if (dateA.isAfter(dateB)) return -1;
              return b.id - a.id;
            })
        );
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load circulars or categories!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleCategoryChange = (e) => setSelectedCategory(e.target.value);
  const handleDetailClose = () => setIsDetailOpen(false);

  // Filter
  const filteredData = circularData.filter((item) => {
    const searchValue = searchTerm.toLowerCase();
    const matchesSearch =
      item.title.toLowerCase().includes(searchValue) ||
      item.description.toLowerCase().includes(searchValue);
    const matchesDate =
      !selectedDate || moment(item.date).isSame(selectedDate, "day");
    const matchesCategory =
      !selectedCategory ||
      categories.find((cat) => cat.id === parseInt(selectedCategory))?.name ===
        item.category;

    return matchesSearch && matchesDate && matchesCategory;
  });

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this circular?"
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(`${API_URL}/circulars/${id}`);
      if (response.status === 200 || response.status === 204) {
        setCircularData((prev) => prev.filter((item) => item.id !== id));
        toast.success("Circular deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to delete circular!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Error deleting circular!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleView = (circular) => {
    setCurrentCircular(circular);
    setIsDetailOpen(true);
  };

  const handleEdit = (circular) => {
    setCurrentCircular(circular);
    setIsAddCircularOpen(true);
  };

  const handleAddCircular = () => setIsAddCircularOpen(true);

  const handleCloseAddCircular = () => {
    setIsAddCircularOpen(false);
    setCurrentCircular(null);
    const fetchCirculars = async () => {
      try {
        const response = await axios.get(`${API_URL}/circulars`);
        setCircularData(
          response.data
            .map((item) => ({
              ...item,
              date: moment(item.fromdate || item.todate, [
                "YYYY-MM-DD",
                "DD/MM/YYYY",
              ]).format("YYYY-MM-DD"),
            }))
            .sort((a, b) => {
              const dateA = moment(a.date);
              const dateB = moment(b.date);
              if (dateB.isAfter(dateA)) return 1;
              if (dateA.isAfter(dateB)) return -1;
              return b.id - a.id;
            })
        );
      } catch (error) {
        console.error("Error fetching circulars:", error);
        toast.error("Failed to fetch circulars!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };
    fetchCirculars();
  };

  const handleRowClick = (circular, event) => {
    if (
      event.target.tagName === "BUTTON" ||
      event.target.tagName === "A" ||
      event.target.closest("button") ||
      event.target.closest("a")
    ) {
      return;
    }
    handleView(circular);
  };

  // ---- Split description into paragraphs ----
  const renderDescription = (description) => {
    if (!description) return "N/A";
    // First split by newlines (\n), then further split each segment by periods
    const newlineParagraphs = description.split("\n").filter((p) => p.trim() !== "");
    const paragraphs = newlineParagraphs
      .flatMap((segment) =>
        segment
          .split(/(?<=\.)\s+/)
          .filter((p) => p.trim() !== "")
      );
    return paragraphs.map((paragraph, index) => (
      <p key={index} className="circular-description">
        {paragraph}
      </p>
    ));
  };

  // ---------- RENDER ----------
  if (isAddCircularOpen) {
    return (
      <div className="add-circular-form">
        <AddCircular
          closeForm={handleCloseAddCircular}
          circular={currentCircular}
          categories={categories}
        />
      </div>
    );
  }

  if (isDetailOpen && currentCircular) {
    return (
      <ViewCircular circular={currentCircular} closeDetail={handleDetailClose} />
    );
  }

  return (
    <div className="circularPage">
      <div className="circularTable">
        <div className="table-header">
          <h3>Circulars</h3>
          <div className="table-header-btm">
            <input
              type="text"
              placeholder="Search"
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <input
              type="date"
              className="date-picker"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value || "")}
            />
            <select
              className="category-filter"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <button onClick={handleAddCircular} className="add-button-circular">
              <FontAwesomeIcon icon={faPlus} />
              Add New
            </button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th className="thead-content">Title</th>
              <th className="thead-content">Description</th>
              <th className="thead-content">Category</th>
              <th className="thead-content">Class</th>
              <th className="thead-content">Section</th>
              <th className="thead-content">Valid Dates</th>
              <th className="thead-content">Attachment</th>
              <th className="thead-content">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  No circulars found
                </td>
              </tr>
            ) : (
              filteredData.map((circular) => {
                const categoryName = categories.find(
                  (cat) => cat.id === parseInt(circular.category)
                )?.name;

                const attachmentURL = circular.pdf_file_path
                  ? `${API_URL}${
                      circular.pdf_file_path.startsWith("/") ? "" : "/"
                    }${circular.pdf_file_path}`
                  : "";

                // Format dates as DD-MM-YY
                const validRange = `${
                  circular.fromdate
                    ? moment(circular.fromdate).format("DD-MM-YY")
                    : "N/A"
                } → ${
                  circular.todate
                    ? moment(circular.todate).format("DD-MM-YY")
                    : "N/A"
                }`;

                return (
                  <tr
                    key={circular.id}
                    onClick={(e) => handleRowClick(circular, e)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="tbody-content">{circular.title}</td>
                    <td className="tbody-content">{renderDescription(circular.description)}</td>
                    <td className="tbody-content">{categoryName || "N/A"}</td>
                    <td className="tbody-content">{circular.class_name || "N/A"}</td>
                    <td className="tbody-content">{circular.section_name || "N/A"}</td>
                    <td className="tbody-content">{validRange}</td>
                    <td className="tbody-content">
                      {attachmentURL ? (
                        <a
                          href={attachmentURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Attachment
                        </a>
                      ) : (
                        "No attachment"
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="view-btn"
                        onClick={() => handleView(circular)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(circular)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(circular.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <ToastContainer position="top-right" pauseOnHover autoClose={3000} />
    </div>
  );
};

export default Circularpage;